import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  bookclubDiscussionsApi,
  type DiscussionReply,
} from '@/services/bookclub-api';
import {
  apiErrorMessage,
  discussionKeys,
  flaggedNotice,
  timeAgo,
} from '@/components/smchs/discussion-lib';

/* =====================================================================
 * Discussion primitives shared by the student desk (inline thread) and
 * the public thread page, so the two never drift apart.
 *
 * Authors may revise or withdraw their own replies; an edit is stamped
 * so it is never silent, and a withdrawal is a soft delete server-side
 * (moderators keep the record). Posting and editing are club-members-
 * only server-side: these components assume the caller has already
 * decided whether to render the composer.
 * ===================================================================== */

const controlButton =
  'px-3 py-1.5 rounded-lg border border-[var(--border)] text-[12.5px] font-medium text-[var(--ink)] hover:border-[var(--primary)] hover:text-[var(--primary)] active:translate-y-px transition-all disabled:opacity-40 disabled:pointer-events-none';

const quietLink =
  'text-[11.5px] font-medium text-[var(--muted)] hover:text-[var(--primary)] transition-colors disabled:opacity-40';

/* ===================================================================== */

export const ReplyList: React.FC<{
  threadId: number;
  replies: DiscussionReply[];
  currentUserId?: number | null;
}> = ({ threadId, replies, currentUserId }) => {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: discussionKeys.thread(threadId) });
    qc.invalidateQueries({ queryKey: discussionKeys.list });
  };

  const saveEdit = async (reply: DiscussionReply) => {
    if (!draft.trim()) return;
    setBusyId(reply.id);
    try {
      const res = await bookclubDiscussionsApi.editReply(threadId, reply.id, draft.trim());
      flaggedNotice(res, 'Reply updated');
      setEditingId(null);
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not save that edit'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (reply: DiscussionReply) => {
    if (confirmId !== reply.id) {
      setConfirmId(reply.id);
      return;
    }
    setConfirmId(null);
    setBusyId(reply.id);
    try {
      await bookclubDiscussionsApi.deleteReply(threadId, reply.id);
      toast.success('Reply deleted');
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not delete that reply'));
    } finally {
      setBusyId(null);
    }
  };

  if (replies.length === 0) {
    return <p className="text-[13.5px] text-[var(--ink-soft)]">No replies yet.</p>;
  }

  return (
    <ul className="space-y-5">
      {replies.map((r) => {
        const mine = currentUserId != null && r.author_id === currentUserId;
        const editing = editingId === r.id;
        const busy = busyId === r.id;
        return (
          <li key={r.id} className="pl-4 border-l-2 border-[var(--border)]">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--muted)]">
              <span>{r.author_name}</span>
              <span>{timeAgo(r.created_at)}</span>
              {r.edited_at && <span className="text-[var(--muted)]">edited</span>}
            </div>

            {editing ? (
              <div className="mt-2">
                <label htmlFor={`edit-${r.id}`} className="sr-only">
                  Edit your reply
                </label>
                <textarea
                  id={`edit-${r.id}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--ink)] px-3.5 py-2.5 text-[14px] leading-[1.55] focus:border-[var(--gold)] focus:outline-none transition-colors resize-y"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => saveEdit(r)}
                    disabled={!draft.trim() || busy}
                    className={controlButton}
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Save'}
                  </button>
                  <button onClick={() => setEditingId(null)} className={quietLink}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="mt-1 text-[14px] leading-[1.6] text-[var(--ink)] whitespace-pre-line">
                  {r.content}
                </p>
                {mine && (
                  <div className="mt-1.5 flex items-center gap-3">
                    <button
                      onClick={() => {
                        setEditingId(r.id);
                        setDraft(r.content);
                        setConfirmId(null);
                      }}
                      disabled={busy}
                      className={quietLink}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(r)}
                      disabled={busy}
                      className={
                        confirmId === r.id
                          ? 'text-[11.5px] font-medium text-[var(--danger)]'
                          : quietLink
                      }
                    >
                      {busy ? 'Working' : confirmId === r.id ? 'Confirm delete' : 'Delete'}
                    </button>
                  </div>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/* ===================================================================== */

export const ReplyComposer: React.FC<{
  threadId: number;
  placeholder?: string;
}> = ({ threadId, placeholder = 'Write a reply' }) => {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const res = await bookclubDiscussionsApi.createReply(threadId, draft.trim());
      flaggedNotice(res, 'Reply posted');
      setDraft('');
      qc.invalidateQueries({ queryKey: discussionKeys.thread(threadId) });
      qc.invalidateQueries({ queryKey: discussionKeys.list });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not post that reply'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <label htmlFor={`reply-${threadId}`} className="sr-only">
        Write a reply
      </label>
      <textarea
        id={`reply-${threadId}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        placeholder={placeholder}
        className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--ink)] placeholder:text-[var(--muted)] px-3.5 py-2.5 text-[14px] leading-[1.55] focus:border-[var(--gold)] focus:outline-none transition-colors resize-y"
      />
      <div className="mt-2 flex justify-end">
        <button onClick={send} disabled={!draft.trim() || sending} className={controlButton}>
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Reply'}
        </button>
      </div>
    </div>
  );
};
